import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { fetchGeolocation, submitLead, getCountryDialCodes, preparLeadData } from '../services/api';

export const PopupForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const countryCodes = getCountryDialCodes();

  useEffect(() => {
    const loadGeolocation = async () => {
      const data = await fetchGeolocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        // Find matching country code
        const matchingCountry = countryCodes.find(c => c.countryCode === data.data.countryCode);
        if (matchingCountry) {
          setFormData(prev => ({ ...prev, countryCode: matchingCountry.code }));
        }
      }
    };
    
    if (isOpen) {
      loadGeolocation();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);
      
      const leadData = preparLeadData(formData, geoData, selectedCountry);

      console.log('Submitting lead data:', leadData);
      
      const result = await submitLead(leadData);
      
      console.log('API Result:', result);

      if (result.success && result.statusCode === 200 && result.customerId) {
        // Full page redirect (external URL)
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        // Show the actual API error message
        const errorMsg = result.message || result.data?.message || 'An error occurred. Please try again.';
        console.error('Form submission failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Get Started Today
          </DialogTitle>
          <p className="text-gray-600 mt-2">
            Fill out the form below and our team will contact you shortly.
          </p>
        </DialogHeader>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Contact Number *</Label>
            <div className="flex space-x-2 mt-1">
              <Select
                value={formData.countryCode}
                onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) {
                    setFormData({ ...formData, phone: value });
                  }
                }}
                placeholder="1234567890"
                maxLength={10}
                pattern="[0-9]{10}"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your trademark needs..."
              rows={4}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};